import { computed } from "vue";
import { Facet } from "@/shared/facets/facets.model";
import { generateQueryFilterForSearch } from "@/shared/facets/facets-filter-generator";
import gql from "graphql-tag";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import { convertAggregationsResult } from "@/shared/aggregations/aggregations-resolver";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import { AggregationParams } from "@/shared/aggregations/aggregations.types.ts";
import { generateAggregationQueryPart } from "@/abilities/documentation/composables/document-search/aggregations-query-generator.ts";

export const useDocumentationsAggregations = ({
    selectedFacets,
    facet,
    searchFields,
    searchContext,
    searchPhrase,
}: AggregationParams) => {
    const { hasAccess: fuzzy } = useHasAccess({ featureID: AccessFeature.fuzzy_search });

    const aggregationVariables = computed(() => {
        const { contentFilter, assetFilter, productId, mechanicId } = generateQueryFilterForSearch(
            selectedFacets?.value ?? [],
            fuzzy.value,
            searchContext?.value,
            searchFields?.value,
            searchPhrase?.value,
            facet.value
        );

        return { contentFilter, assetFilter, productId, mechanicId };
    });

    const generateDynamicSearchContentsOnlyAggregationsQuery = (facet: Facet, keywordSearchValue: string = "") => {
        const aggregations = generateAggregationQueryPart(facet, keywordSearchValue);

        if (searchContext?.value?.mechanic) {
            return gql`query searchDocumentsForMechanicAggregations($mechanicId: String!, $contentFilter: ContentFilter ) {
                mechanic(id: $mechanicId) {
                    id
                    relatedContents(filter: $contentFilter)
                    { ${aggregations} }
                }
            }`;
        }

        if (searchContext?.value.asset && searchContext?.value.product) {
            return gql`query searchDocumentsForAssetWithProductOnlyAggregations($assetFilter: AssetFilter!, $contentFilter: ContentFilter!) {
                assets(filter: $assetFilter) {
                    assets(first:1) {
                        cursor
                        node {
                            id
                            relatedContents(filter: $contentFilter) 
                            { ${aggregations} }                       
                        }
                    }
                }
            } `;
        }
        if (searchContext?.value.product) {
            return gql`
            query searchRelatedContentsProductOnlyAggregations($productId: String!, $contentFilter: ContentFilter!) {
                product(id: $productId) {
                    relatedContents(filter: $contentFilter)                   
                    { ${aggregations} }
                }
            } `;
        }
        return gql`query searchContentsOnlyAggregations($contentFilter: ContentFilter) {
            contents(filter: $contentFilter)
            { ${aggregations} }
        }`;
    };

    const fetchAggregation = async (keywordSearchValue: string) => {
        if (!facet.value || facet.value.facetType === "dateTime") return {};

        const aggregationResult = await consumerClient.query({
            query: generateDynamicSearchContentsOnlyAggregationsQuery(facet.value, keywordSearchValue),
            variables: aggregationVariables.value,
        });

        const convertedAggregations = convertAggregationsResult(
            facet.value,
            aggregationResult.data?.contents?.aggregations ??
                aggregationResult.data?.product?.relatedContents?.aggregations ??
                aggregationResult.data?.assets?.assets[0]?.node?.relatedContents?.aggregations ??
                aggregationResult.data?.mechanic?.relatedContents?.aggregations
        );

        return convertedAggregations[facet.value.id];
    };

    return {
        fetchAggregation,
        aggregationVariables,
    };
};
